/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-non-null-assertion */
/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useEffect, useMemo, useState } from "react";
import InternetDetailsCard from "./InternetDetailsCard";
import { useLocation, useSearchParams } from "react-router-dom";
import { mapConnectionDetailToSections } from "kontrolEdge/constants/constants";
import { useProcessingOverlay } from "@shared/hooks/useProcessingOverlay";
import { useAxiosAuth0 } from "@shared/config/useAxiosAuth0";
import { fetchConnectionBgpAPI, fetchConnectionErrorsAPI, fetchConnectionSubscriptionAPI, fetchInternetConnectionAPI, fetchInternetConnectionSubnetsAPI } from "../requests/request";
import { useAppSelector } from "@redux/hooks";
import NotificationsView from "kontrolEdge/components/Shared/Notifications/NotificationsView";
import { RootState } from "@redux/store/store";
import InternetBandwidthChart from "./InternetUtilizationChart/BandwidthChart";

// 1) The overall response shape
export interface InternetConnectionResponse {
  success: boolean;
  message: string;
  data: RawConnectionDetail;
}

export interface Subnet {
  id: number;
  ip: string;
  mask: string;
  status: string;
}
export interface BgpNeighbor {
  peeringType: string;
  neighborIpAddress: string;
  prefixesAccepted: number;
  remoteRouterId: string;
  localASN: number;
  remoteASN: number;
  advertisedPaths: number;
  state: { isConnected: boolean };
  sourceIp?: string;
}

export interface BgpNeighborsPayload {
  serviceType?: string;
  bgpNeighbors: BgpNeighbor[];
}

export interface BgpDetail {
  // mirror the shape returned by fetchConnectionBgpAPI:
  peeringType: string;
  neighborIpAddress: string;
  state: { isConnected: boolean };
}

export interface LatencyDetail {
  destination: string;
  timestamp: string;
  unit: string;
  value: number
}

export interface SubscriptionDetail {
  subscriptionId: string;
  // …other fields…
}

// 2) The actual “data” payload
export interface RawConnectionDetail {
  id: number;
  mbps: number;
  vlan: number;
  automaticVlan: boolean;
  redundancy: boolean;
  routing: string;
  terms: number;
  autoRenew: boolean;
  p2pAddressing: {
    mask: string;
    peerIp: string;
    gateway: string;
    network: string;
    maskSecondary?: string;
    peerIpSecondary?: string;
    gatewaySecondary?: string;
    networkSecondary?: string;
  };
  bgp?: BgpDetail; // legacy single-peer shape (keep for fallback)
  bgpServiceType?: string;         // NEW
  bgpNeighbors?: BgpNeighbor[];
  subscriptionId: string;
  port: {
    id: number;
    name: string;
    datacenter: {
      datacenterId: string;
      name: string;
      displayName: string;
      direction: string;
      gps: string;
      capacityKW: string;
      phase: string;
      cage: string;
      rackId: string;
      rackCapacity: string;
      patchPanelODF: string;
      rackSecurityPin: string;
      status: string;
      visible: boolean;
      providerCode: string;
      city: string;
      providerId: number;
      createdAt: string;
      updatedAt: string;
    };
  };
  lag: null;
  performanceMetrics?: {
    errors: number;
    latency?: LatencyDetail
  };
  subscription?: SubscriptionDetail;
  subnets: Array<{
    id: number;
    ip: string;
    mask: string;
    status: string;
  }>;
}

type LocationState = { connectionDetail?: RawConnectionDetail, tenantOverride?: number; };

const InternetDetails: React.FC = () => {
	//Grab the raw Location object
	const location = useLocation();
  const reduxTenantId = useAppSelector((state) => state.selection.tenantId);
	//Assert its state shape
	const { connectionDetail: preloaded, tenantOverride } =
		(location.state as LocationState) || {};
  const [searchParams] = useSearchParams();
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const connectionId = searchParams.get("connectionId")!;

	//Seed from navigation‑state if available
	const [detail, setDetail] = useState<RawConnectionDetail | undefined>(preloaded);
	const [isFetching, setIsFetching] = useState(false);
	const overlay = useProcessingOverlay(isFetching);
	const api = useAxiosAuth0();
  const userProfile = useAppSelector((state: RootState) => state.userProfile);
  const { canAccessAlerts } = userProfile;
  const effectiveTenantId = tenantOverride ?? reduxTenantId;
  const { tenantId } = useAppSelector((state) => state.selection);

  const { performanceData } = useAppSelector(state => state.connection);
  //Grab the first reading in the array:
  const rawReadings = performanceData?.latencyReadings;
  const firstLatency: LatencyDetail | undefined = Array.isArray(rawReadings)
    ? rawReadings[0]
    : undefined;

    const sections = useMemo(
      () => mapConnectionDetailToSections({ data: detail }, firstLatency),
      [detail, firstLatency]
    );


    useEffect(() => {
      //Re-run whenever connectionId or refresh changes
      if (!connectionId) return;
      let cancelled = false;
      setIsFetching(true);
    
      (async () => {
        try {
          //Core detail – show it immediately
          const coreResp = await fetchInternetConnectionAPI(api, connectionId, effectiveTenantId);
          const fresh: RawConnectionDetail = coreResp.data.data;
          if (cancelled) return;
          setDetail(fresh);
    
          //Extras in parallel
          const [
            errorsSettled,
            subnetsSettled,
            bgpSettled,
            subscriptionSettled
          ] = await Promise.allSettled([
            fetchConnectionErrorsAPI(api, connectionId, tenantId),
            fetchInternetConnectionSubnetsAPI(api, connectionId,tenantId),
            fetchConnectionBgpAPI(api, connectionId, tenantId),
            fetchConnectionSubscriptionAPI(api, connectionId, tenantId),
          ]);
    
          //Merge successful results
          if (errorsSettled.status === 'fulfilled') {
            fresh.performanceMetrics = {
              ...fresh.performanceMetrics,
              errors: errorsSettled.value.data.data.crcErrors
            };
          } else {
            console.warn('CRC errors failed:', errorsSettled.reason);
          }
    
          if (subnetsSettled.status === 'fulfilled') {
            fresh.subnets = subnetsSettled.value.data.data;
          } else {
            console.warn('Subnets failed:', subnetsSettled.reason);
          }
    
          // if (bgpSettled.status === 'fulfilled') {
          //   fresh.bgp = bgpSettled.value.data.data;
          // } else {
          //   console.warn('BGP failed:', bgpSettled.reason);
          // }

          if (bgpSettled.status === 'fulfilled') {
            const payload: BgpNeighborsPayload | undefined = bgpSettled.value?.data?.data;
            const neighbors = Array.isArray(payload?.bgpNeighbors) ? payload!.bgpNeighbors : [];
            (fresh as any).bgpServiceType = payload?.serviceType;
            (fresh as any).bgpNeighbors = neighbors;
          
            // Back-compat: keep first neighbor in legacy field if someone still reads data.bgp
            fresh.bgp = neighbors[0] ?? undefined;
          } else {
            console.warn('BGP failed:', bgpSettled.reason);
          }
    
          if (subscriptionSettled.status === 'fulfilled') {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (fresh as any).subscription = subscriptionSettled.value.data.data;
          } else {
            console.warn('Subscription fetch failed:', subscriptionSettled.reason);
          }
    
          if (cancelled) return;
          //Push the merged object back into state
          setDetail({ ...fresh });
    
        } catch (err) {
          console.error('Error loading InternetDetails:', err);
        } finally {
          if (!cancelled) setIsFetching(false);
        }
      })();
    
      return () => { cancelled = true; };
    }, [api, connectionId]);
		
		if (isFetching || !detail) return <>{overlay}</>;

	return (
		<>
			<div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-4 auto-rows-auto">
			{sections.map((section, idx) => (
				<InternetDetailsCard key={idx} data={section} />
			))}
			</div>
			{/* Internet Utilization chart */}
			<InternetBandwidthChart connectionId={connectionId} />
      {/* {canAccessAlerts && connectionId && (
        <NotificationsView serviceType="internet" serviceId={connectionId}/>
      )} */}
		</>
	);
};

export default InternetDetails;
